import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { CrmFieldService } from '../../../../shared-services/crm-services/field.service';
import { CrmObjectService } from '../../../../shared-services/crm-services/crm-object-service/crm-object.service';
import {
  CrmDependencies,
  CrmInjectionToken,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
} from '../../../../tokens-and-interfaces';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import {
  CampaignService,
  RecipientCampaignService,
  SenderType,
  SortDirection,
  SortField,
  SortOptions,
  Statuses,
} from '@vendasta/campaigns';
import { SenderInterface } from '@vendasta/email';
import { VaFormsModule } from '@vendasta/forms';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { Scrollable } from '@vendasta/rx-utils';
import { VaFilterModule } from '@vendasta/uikit';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, take } from 'rxjs/operators';
import { CrmObjectInterface } from '@vendasta/crm';
import { StartCampaignDialogComponent } from './start-campaign-dialog.component';
import { MatExpansionModule } from '@angular/material/expansion';

export interface Campaign {
  id: string;
  title: string;
  updated: string;
}

export interface contactCard {
  id: string;
}

@Component({
  selector: 'crm-add-campaign-modal',
  templateUrl: './add-campaign-dialog.component.html',
  styleUrls: ['./add-campaign-dialog.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    MatDialogModule,
    GalaxyEmptyStateModule,
    MatIconModule,
    GalaxyFormFieldModule,
    MatRadioModule,
    FormsModule,
    VaFormsModule,
    MatInputModule,
    GalaxyButtonLoadingIndicatorModule,
    MatProgressSpinnerModule,
    VaFilterModule,
    GalaxyInfiniteScrollTriggerModule,
    ReactiveFormsModule,
    GalaxyTooltipModule,
    GalaxyAlertModule,
    MatExpansionModule,
  ],
  providers: [
    RecipientCampaignService,
    CrmFieldService,
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Contact'),
    },
  ],
})
export class AddCampaignDialogComponent implements OnInit {
  crmObject$: Observable<CrmObjectInterface>;
  searchControl = new UntypedFormControl();
  campaigns$$ = new BehaviorSubject<Campaign[]>([] as Campaign[]);
  campaigns$ = this.campaigns$$.asObservable();
  criteria$ = this.searchControl.valueChanges.pipe(startWith(''), distinctUntilChanged(), debounceTime(300));
  selectedCampaign: Campaign;
  scrollable: Scrollable<Campaign, string>;
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  placeholder = '';

  constructor(
    private readonly translate: TranslateService,
    private readonly objectService: CrmObjectService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    readonly ref: MatDialogRef<AddCampaignDialogComponent>,
    private readonly campaignService: CampaignService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) readonly modalData: contactCard,
  ) {
    this.crmObject$ = objectService.getMultiObject('Contact', [this.modalData.id || '']).pipe(
      map((resp) => {
        const object = resp?.crmObjects?.[0];
        if (resp?.crmObjects?.length === 0 || !object) {
          return {};
        }
        return object;
      }),
    );
  }

  ngOnInit() {
    combineLatest([this.config.namespace$, this.config.parentNamespace$])
      .pipe(take(1))
      .subscribe(([namespace, parentNamespace]) => {
        let senderType = SenderType.SENDER_TYPE_PARTNER;
        if (parentNamespace) {
          senderType = SenderType.SENDER_TYPE_BUSINESS;
        }
        this.scrollable = this.getScrollableList(namespace, senderType);
        this.campaigns$ = this.scrollable.items$;
        this.placeholder = this.translate.instant('CAMPAIGNS.CONTACTS.MODAL.SEARCH_PLACEHOLDER');
      });
  }

  close() {
    this.ref.close();
  }

  loadNext() {
    this.scrollable?.loadMore();
  }

  onItemChange(campaign: Campaign) {
    this.selectedCampaign = campaign;
  }

  getScrollableList(senderId: string, senderType: SenderType) {
    return new Scrollable<Campaign, string>(this.criteria$, (criteria, cursor) => {
      return this.loadMore(criteria, cursor, senderId, senderType);
    });
  }

  loadMore(criteria: string, cursor: string, senderId: string, senderType: SenderType) {
    this.loading$$.next(true);
    return this.campaignService
      .listCampaignsV2(
        { type: senderType, id: senderId } as SenderInterface,
        [Statuses.STATUSES_ACTIVE, Statuses.STATUSES_PUBLISHED],
        criteria,
        { cursor: cursor, pageSize: 10 },
        { field: SortField.SORT_FIELDS_UPDATED_AT, direction: SortDirection.SORT_ORDER_DESCENDING } as SortOptions,
      )
      .pipe(
        map((response) => {
          this.loading$$.next(false);
          return {
            items: response.campaigns.map((campaign) => {
              return {
                id: campaign.campaignId,
                title: campaign.name,
                updated: campaign.updated.toISOString(),
              } as Campaign;
            }),
            cursor: response.metadata?.nextCursor ?? null,
            hasMore: response.metadata?.hasMore ?? false,
          };
        }),
        catchError(() => of({ items: [] as Array<Campaign>, cursor: '', hasMore: false })),
      );
  }

  openStartCampaignDialog() {
    this.ref.close();
    this.dialog.open(StartCampaignDialogComponent, {
      data: {
        campaign: this.selectedCampaign,
        id: this.modalData.id,
      },
      width: '500px',
    });
  }
}
